import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, inject, ViewChild } from '@angular/core';
import { provideComponentStore } from '@ngrx/component-store';
import { PasLayoutSidebarHolderComponent, PasSidebarHandleService } from '@porsche-aftersales/frontend-toolbox';
import { ColDef } from 'ag-grid-community';
import { Observable } from 'rxjs';

import { TranslatePipe } from '../../../pipes/translate.pipe';
import { getDateAsFormattedString } from '../../../services/ag-grid/formatter-service';
import { UserRights } from '../../../services/user-service/user-rights.enum';
import { UserService } from '../../../services/user-service/user.service';
import { GenericModalContentComponent } from '../../../shared/generic-modal-content/generic-modal-content.component';
import { ColDefTemplate, fromColDefTemplates, TheGridComponent } from '../../../shared/the-grid/the-grid.component';

import { ButtonCellRendererComponent } from './button-cell-renderer.component';
import { SupplierConditionEditorComponent } from './supplier-condition-editor.component';
import { ASTERISK, SupplierCondition, SupplierConditionStore } from './supplier-condition.store';

const asteriskOrValueFormatter = (params: any): string => params?.value ?? ASTERISK;

export type EnumType<T> = {
  [K in keyof T]: T[K];
};

@Component({
  selector: 'app-supplier-condition',
  templateUrl: './supplier-condition.component.html',
  styleUrls: ['./supplier-condition.component.scss'],
  imports: [
    CommonModule,
    SupplierConditionEditorComponent,
    TheGridComponent,
    GenericModalContentComponent,
    TranslatePipe,
    PasLayoutSidebarHolderComponent,
  ],
  providers: [provideComponentStore(SupplierConditionStore)],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SupplierConditionComponent implements AfterViewInit {
  private readonly supplierConditionStore = inject(SupplierConditionStore);
  private readonly sidebarHandleService = inject(PasSidebarHandleService);
  private readonly userService = inject(UserService);

  @ViewChild('modalTemplate')
  public modalTemplate: ElementRef;

  public readonly supplierCondition$: Observable<SupplierCondition[]> = this.supplierConditionStore.supplierCondition$;
  public readonly loading$: Observable<boolean> = this.supplierConditionStore.loading$;

  public readonly isFormValidAndModalNotOpenInDisplayOnlyMode$ =
    this.supplierConditionStore.isFormValidAndModalNotOpenInDisplayOnlyMode$;

  public readonly defaultColumnDef: ColDef<any> = DEFAULT_COL_DEF;
  public readonly columnDefs: ColDef[] = fromColDefTemplates(COL_DEF_TEMPLATES(this.userService));

  public readonly canAddAndEditSupplierCondition: boolean = this.userService.hasRight(UserRights.RECOURSE_BOOKING);

  public ngAfterViewInit(): void {
    // we actually have to set it (every time). TODO: think about how to just do that once.
    this.sidebarHandleService.setVisibleInCurrentView(true);

    this.supplierConditionStore.patchState({ modalTemplate: this.modalTemplate });
  }

  public addSupplierCondition(): void {
    this.supplierConditionStore.addSupplierCondition();
  }

  public onConfirm(): void {
    this.supplierConditionStore.saveOrUpdateSupplierCondition();
  }

  public onCancel(): void {
    this.supplierConditionStore.cancelSupplierConditionEditing();
  }
}

const DEFAULT_COL_DEF: ColDef<any> = {
  resizable: true,
  sortable: true,
  floatingFilter: true,
  unSortIcon: true,
  filter: 'agTextColumnFilter',
  flex: 1,
};

const numberFormatter =
  (userService: UserService) =>
  (params: any): string => {
    if (params.value == null || params.value === '') return '';
    const decimalSeparator = userService.userLanguage.startsWith('en') ? '.' : ',';
    return params.value.toString().replace('.', decimalSeparator);
  };

const COL_DEF_TEMPLATES = (userService: UserService): ColDefTemplate<SupplierCondition>[] => [
  {
    headerNameAndTooltipKey: 'supplierCondition.supplierNumberShort',
    typedField: 'supplierNumber',
    valueFormatter: asteriskOrValueFormatter,
  },
  {
    headerNameAndTooltipKey: 'supplierCondition.supplierNameShort',
    typedField: 'supplierName',
    valueFormatter: asteriskOrValueFormatter,
  },
  {
    headerNameAndTooltipKey: 'supplierCondition.materialCostsFactorShort',
    typedField: 'materialCostsFactor',
  },
  {
    headerNameAndTooltipKey: 'supplierCondition.laborCostsFactorShort',
    typedField: 'laborCostsFactor',
  },
  {
    headerNameAndTooltipKey: 'supplierCondition.handlingChargeCostsFactorShort',
    typedField: 'handlingChargeCostsFactor',
  },
  {
    headerNameAndTooltipKey: 'supplierCondition.handlingSupplierCostsFactorShort',
    typedField: 'handlingSupplierCostsFactor',
  },
  {
    headerNameAndTooltipKey: 'supplierCondition.landingCostsFactorShort',
    typedField: 'landingCostsFactor',
  },
  {
    headerNameAndTooltipKey: 'supplierCondition.otherCostsShort',
    typedField: 'otherCosts',
    valueFormatter: numberFormatter(userService),
  },
  {
    headerNameAndTooltipKey: 'supplierCondition.thirdPartyCostsFactorShort',
    typedField: 'thirdPartyCostsFactor',
  },
  {
    headerNameAndTooltipKey: 'supplierCondition.thirdPartyMaterialCostsFactorShort',
    typedField: 'thirdPartyMaterialCostsFactor',
  },
  {
    headerNameAndTooltipKey: 'supplierCondition.createDateShort',
    typedField: 'createDate',
    valueGetter: p => getDateAsFormattedString(p.data.createDate),
  },
  {
    headerNameAndTooltipKey: 'supplierCondition.lastEditDateShort',
    typedField: 'lastEditDate',
    valueGetter: p => getDateAsFormattedString(p.data.lastEditDate),
  },
  {
    headerNameAndTooltipKey: undefined,
    headerClass: ['ag-header-last-cell'],
    cellRenderer: ButtonCellRendererComponent,
    filter: false,
    menuTabs: [],
    unSortIcon: false,
    width: 80,
  },
];
